import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const Landing: React.FC = () => {
  const navigate = useNavigate();

  const glassCard = {
    borderRadius: 3,
    backdropFilter: "blur(14px)",
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.75), rgba(255,255,255,0.4))",
    border: "1px solid rgba(255,255,255,0.2)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(to bottom, #f8fafc, #eef2ff)", // ✅ MATCH APP STYLE
      }}
    >
      {/* HERO */}
      <Container maxWidth="md" sx={{ textAlign: "center", py: 12 }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          🎬 Smart AI Video Processing
        </Typography>

        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Transcribe, compress, trim, and convert videos instantly using AI.
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/register")}
          >
            Get Started Free
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/login")}
          >
            Login
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="center" mt={3}>
          <Chip label="No Credit Card Required" size="small" />
          <Chip label="Free Plan Available" size="small" />
        </Stack>
      </Container>

      {/* FEATURES */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3}>
          {[
            {
              title: "🧠 Transcribe Videos",
              desc: "Convert speech into accurate text using AI (Whisper).",
            },
            {
              title: "📦 Compress Videos",
              desc: "Reduce file size dramatically while keeping quality.",
            },
            {
              title: "✂️ Trim Videos",
              desc: "Cut specific segments from videos instantly.",
            },
            {
              title: "🎵 Extract Audio",
              desc: "Convert videos into high-quality MP3 files.",
            },
          ].map((item) => (
            <Grid item xs={12} md={3} key={item.title}>
              <Card sx={glassCard}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* PRICING */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Simple & Transparent Pricing
        </Typography>

        <Typography align="center" color="text.secondary" sx={{ mb: 6 }}>
          Choose a plan that fits your needs
        </Typography>

        <Grid container spacing={4}>

          {/* FREE */}
          <Grid item xs={12} md={3}>
            <Card sx={glassCard}>
              <CardContent>
                <Typography variant="h6">Free</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ my: 2 }}>
                  €0
                </Typography>

                <Stack spacing={1} mb={3}>
                  <Typography>✔ 3 tasks/day</Typography>
                  <Typography>✔ Basic compression</Typography>
                  <Typography>✔ Standard speed</Typography>
                </Stack>

                <Button fullWidth variant="outlined" onClick={() => navigate('/app/premium')}>
                  Start Free
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* MONTHLY */}
          <Grid item xs={12} md={3}>
            <Card sx={{ ...glassCard, border: "2px solid #667eea" }}>
              <CardContent>
                <Typography variant="h6">Monthly</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ my: 2 }}>
                  $9.99
                </Typography>

                <Stack spacing={1} mb={3}>
                  <Typography>✔ Unlimited processing</Typography>
                  <Typography>✔ Faster speed</Typography>
                  <Typography>✔ Better quality</Typography>
                </Stack>

                <Button fullWidth variant="contained" onClick={() => navigate('/app/premium')}>
                  Upgrade
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* YEARLY */}
          <Grid item xs={12} md={3}>
            <Card sx={glassCard}>
              <CardContent>
                <Typography variant="h6">Yearly</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ my: 2 }}>
                  $49.99
                </Typography>

                <Stack spacing={1} mb={3}>
                  <Typography>✔ Save 58%</Typography>
                  <Typography>✔ Unlimited usage</Typography>
                  <Typography>✔ Priority processing</Typography>
                </Stack>

                <Button fullWidth variant="outlined" onClick={() => navigate('/app/premium')}>
                  Choose Plan
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* LIFETIME */}
          <Grid item xs={12} md={3}>
            <Card sx={glassCard}>
              <CardContent>
                <Typography variant="h6">Lifetime</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ my: 2 }}>
                  $99.99
                </Typography>

                <Stack spacing={1} mb={3}>
                  <Typography>✔ One-time payment</Typography>
                  <Typography>✔ Lifetime access</Typography>
                  <Typography>✔ All future updates</Typography>
                </Stack>

                <Button fullWidth variant="outlined" onClick={() => navigate('/app/premium')}>
                  Get Lifetime
                </Button>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>

      {/* TRUST */}
      <Box textAlign="center" py={4}>
        <Typography variant="body2" color="text.secondary">
          🔒 Secure payments powered by Stripe. We do not store your payment details.
        </Typography>
      </Box>

      <Divider />

      {/* FOOTER */}
      <Box textAlign="center" py={4}>
        <Stack direction="row" spacing={3} justifyContent="center">
          <Button size="small" onClick={() => navigate("/terms")}>
            Terms
          </Button>
          <Button size="small" onClick={() => navigate("/privacy")}>
            Privacy
          </Button>
          <Button size="small" onClick={() => navigate("/contact")}>
            Contact Support
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          © 2026 Smart Video Processor
        </Typography>
      </Box>
    </Box>
  );
};

export default Landing;